/**
 * Session Manager for Analytics-Driven Push Notifications
 * Handles user fingerprinting, session tracking, and push subscription
 */

import { PushManager } from "./push-manager";

interface SessionData {
  userId: string;
  sessionId: string;
  startTime: number;
  questionCount: number;
  correctCount: number;
  currentVerb?: string;
  streak: number;
}

interface PendingSessionEnd {
  userId: string;
  sessionId: string;
  endTime: string;
  duration: number;
  questionCount: number;
  correctCount: number;
  accuracy: number;
  completedVerbs: string[];
  timestamp: number;
}

interface PerformanceData {
  userId: string;
  verb: string;
  questionType: string;
  tense: string;
  pronoun: string;
  isCorrect: boolean;
  responseTime: number;
  currentStreak: number;
  difficulty: number;
}

export class SessionManager {
  private session: SessionData | null = null;
  private pushManager: PushManager;
  private apiBaseUrl: string;
  private userId: string;
  private verbName: string;
  private readonly PENDING_SESSIONS_KEY = "german-trainer-pending-sessions";

  constructor(verbName: string, apiBaseUrl?: string) {
    this.verbName = verbName;
    // Use local backend in development
    this.apiBaseUrl =
      apiBaseUrl ||
      (window.location.hostname === "localhost"
        ? "http://localhost:3000"
        : "https://german-vibes-api.aek.dk");
    this.pushManager = new PushManager(this.apiBaseUrl);
    this.userId = this.generateOrGetUserId();

    // Try to send any pending session ends on initialization
    this.processPendingSessionEnds();
  }

  /**
   * Generate a stable user ID based on browser fingerprinting
   */
  private generateOrGetUserId(): string {
    const storageKey = "german-trainer-user-id";
    let userId = localStorage.getItem(storageKey);

    if (!userId) {
      // Generate fingerprint based on browser characteristics
      const fingerprint = this.generateFingerprint();
      userId = this.hashString(fingerprint).substring(0, 16);
      localStorage.setItem(storageKey, userId);
    }

    return userId;
  }

  /**
   * Generate browser fingerprint for user identification
   */
  private generateFingerprint(): string {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.textBaseline = "top";
      ctx.font = "14px Arial";
      ctx.fillText("German Trainer Fingerprint", 2, 2);
    }

    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + "x" + screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      !!window.sessionStorage,
      !!window.localStorage,
      !!window.indexedDB,
      canvas.toDataURL(),
    ].join("|");

    return fingerprint;
  }

  /**
   * Simple hash function for fingerprint
   */
  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(16);
  }

  /**
   * Start a new learning session
   */
  async startSession(): Promise<void> {
    const sessionId =
      Date.now().toString() + Math.random().toString(36).substr(2, 9);

    this.session = {
      userId: this.userId,
      sessionId,
      startTime: Date.now(),
      questionCount: 0,
      correctCount: 0,
      streak: this.getCurrentStreak(),
    };

    try {
      await this.sendToBackend("/api/session", {
        method: "POST",
        body: JSON.stringify({
          userId: this.userId,
          sessionId,
          verb: this.verbName,
          startTime: new Date().toISOString(),
          userAgent: navigator.userAgent,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      });

      console.log("📊 Session started:", this.session);
    } catch (error) {
      console.error("Failed to start session:", error);
    }
  }

  /**
   * End current learning session
   */
  async endSession(): Promise<void> {
    if (!this.session) return;

    const duration = Date.now() - this.session.startTime;
    const accuracy =
      this.session.questionCount > 0
        ? this.session.correctCount / this.session.questionCount
        : 0;

    const sessionEndData: PendingSessionEnd = {
      userId: this.userId,
      sessionId: this.session.sessionId,
      endTime: new Date().toISOString(),
      duration,
      questionCount: this.session.questionCount,
      correctCount: this.session.correctCount,
      accuracy,
      completedVerbs: this.session.currentVerb
        ? [this.session.currentVerb]
        : [],
      timestamp: Date.now(),
    };

    try {
      // Try to send immediately
      await this.sendSessionEndToBackend(sessionEndData);
    } catch {
      // Store for later retry if network fails
      this.storePendingSessionEnd(sessionEndData);
    }

    this.session = null;
  }

  /**
   * Track performance for a specific question
   */
  async trackPerformance(data: Omit<PerformanceData, "userId">): Promise<void> {
    if (!this.session) return;

    // Update session stats
    this.session.questionCount++;
    if (data.isCorrect) {
      this.session.correctCount++;
      this.session.streak++;
    } else {
      this.session.streak = 0;
    }
    this.session.currentVerb = data.verb;

    // Store streak in localStorage
    localStorage.setItem(
      "german-trainer-streak",
      this.session.streak.toString()
    );

    try {
      await this.sendToBackend("/api/performance", {
        method: "POST",
        body: JSON.stringify({
          userId: this.userId,
          sessionId: this.session.sessionId,
          ...data,
          timestamp: new Date().toISOString(),
        }),
      });

      console.log("📈 Performance tracked:", data);
    } catch (error) {
      console.error("Failed to track performance:", error);
    }
  }

  /**
   * Initialize push notifications
   */
  async initializePushNotifications(): Promise<boolean> {
    return await this.pushManager.initialize(this.userId);
  }

  /**
   * Get current streak from localStorage
   */
  private getCurrentStreak(): number {
    const streak = localStorage.getItem("german-trainer-streak");
    return streak ? parseInt(streak, 10) : 0;
  }

  /**
   * Send request to backend
   */
  private async sendToBackend(
    endpoint: string,
    options: RequestInit
  ): Promise<any> {
    const response = await fetch(`${this.apiBaseUrl}${endpoint}`, {
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(
        `Backend request failed: ${response.status} ${response.statusText}`
      );
    }

    return response.json();
  }

  /**
   * Get user ID
   */
  getUserId(): string {
    return this.userId;
  }

  /**
   * Get current session info
   */
  getSession(): SessionData | null {
    return this.session;
  }

  /**
   * Get user statistics
   */
  async getUserStats(): Promise<any> {
    try {
      return await this.sendToBackend(`/api/stats/${this.userId}`, {
        method: "GET",
      });
    } catch (error) {
      console.error("Failed to get user stats:", error);
      return null;
    }
  }

  /**
   * Get learning insights
   */
  async getLearningInsights(): Promise<any> {
    try {
      return await this.sendToBackend(`/api/insights/${this.userId}`, {
        method: "GET",
      });
    } catch (error) {
      console.error("Failed to get learning insights:", error);
      return null;
    }
  }

  /**
   * Process any pending session ends from previous sessions
   */
  private async processPendingSessionEnds(): Promise<void> {
    const pending = this.getPendingSessionEnds();
    if (pending.length === 0) return;

    const successful: number[] = [];

    for (let i = 0; i < pending.length; i++) {
      const sessionData = pending[i];

      // Skip sessions older than 24 hours
      if (Date.now() - sessionData.timestamp > 24 * 60 * 60 * 1000) {
        successful.push(i);
        continue;
      }

      try {
        await this.sendSessionEndToBackend(sessionData);
        successful.push(i);
      } catch {
        // Keep it for next retry
      }
    }

    // Remove successfully sent sessions
    if (successful.length > 0) {
      const remaining = pending.filter(
        (_, index) => !successful.includes(index)
      );
      if (remaining.length === 0) {
        localStorage.removeItem(this.PENDING_SESSIONS_KEY);
      } else {
        localStorage.setItem(
          this.PENDING_SESSIONS_KEY,
          JSON.stringify(remaining)
        );
      }
    }
  }

  /**
   * Get pending session ends from localStorage
   */
  private getPendingSessionEnds(): PendingSessionEnd[] {
    try {
      const stored = localStorage.getItem(this.PENDING_SESSIONS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  /**
   * Store session end data for later retry
   */
  private storePendingSessionEnd(sessionData: PendingSessionEnd): void {
    try {
      const pending = this.getPendingSessionEnds();
      pending.push(sessionData);

      // Keep only the last 10 pending sessions to avoid storage bloat
      const trimmed = pending.slice(-10);

      localStorage.setItem(this.PENDING_SESSIONS_KEY, JSON.stringify(trimmed));
    } catch {
      // Silent fail for storage issues
    }
  }

  /**
   * Send session end data to backend
   */
  private async sendSessionEndToBackend(
    sessionData: PendingSessionEnd
  ): Promise<void> {
    await this.sendToBackend("/api/session", {
      method: "POST",
      body: JSON.stringify(sessionData),
    });
  }
}
